import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  CreateGoodsReceiptMutation,
  GoodsReceipt,
  GoodsReceiptCreateFailure,
  GoodsReceiptCreateInput,
  GoodsReceiptCreateSuccess,
  GoodsReceiptMutationCreateArgs,
} from "@/graphql/codegen/graphql"
import { CreateGoodsReceiptDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCreateGoodsReceiptReturn {
  create: (input: GoodsReceiptCreateInput) => Promise<GoodsReceipt>
  isCreating: boolean
  error?: CombinedError
}

export const useCreateGoodsReceipt = (onSuccess?: () => void): UseCreateGoodsReceiptReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching, error }, executeMutation] = useMutation<
    CreateGoodsReceiptMutation,
    GoodsReceiptMutationCreateArgs
  >(CreateGoodsReceiptDocument)

  const create = async (input: GoodsReceiptCreateInput): Promise<GoodsReceipt> => {
    const { data, error } = await executeMutation({ input })
    const output = data?.goodsReceipt.create
    const hasError = error || output?.__typename === "GoodsReceiptCreateFailure"

    if (hasError) {
      const errorMessage =
        error?.message ?? (output as GoodsReceiptCreateFailure).error ?? "Failed to create goods receipt."
      notify({
        type: NotificationType.Error,
        message: errorMessage,
      })
      throw new Error(errorMessage)
    }

    const goodsReceipt = (output as GoodsReceiptCreateSuccess).goodsReceipt
    notify({
      type: NotificationType.Success,
      message: `Advanced Goods Receipt #${goodsReceipt.agrNumber} successfully created.`,
    })
    onSuccess?.()
    return goodsReceipt
  }

  return {
    create,
    isCreating: fetching,
    error,
  }
}
